import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { useNotification } from '../../context/NotificationContext';
import { sendShareableLinkEmail } from '../services/sendShareableLinkEmail';
import { getEmployee } from '../../api/companyRecords';
import './ManualBookingSuccessPage.css';

function hexToRgbString(hex) {
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized[0] + normalized[0] + normalized[1] + normalized[1] + normalized[2] + normalized[2];
  }
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function formatPhoneNumber(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const DashboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
  </svg>
);

const EMAIL_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

function formatCooldown(msLeft) {
  if (msLeft <= 0) return null;
  const totalSeconds = Math.ceil(msLeft / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const ManualBookingSuccessPage = () => {
  const { showToast } = useNotification();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailCooldownUntil, setEmailCooldownUntil] = useState(null);
  const [emailCooldownLabel, setEmailCooldownLabel] = useState('');
  const [fetchedStaffPhotoURL, setFetchedStaffPhotoURL] = useState(null);
  const [staffRating, setStaffRating] = useState(null);
  const { primaryColor, mode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingData, shareableLink } = location.state || {};

  useEffect(() => {
    if (!bookingData) {
      navigate('/app/overview', { replace: true });
    }
  }, [bookingData, navigate]);

  const themeVars = {
    '--success-primary': primaryColor || '#1976d2',
    '--success-primary-rgb': hexToRgbString(primaryColor || '#1976d2'),
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      showToast('Link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleEmailLink = async () => {
    try {
      setSendingEmail(true);
      const result = await sendShareableLinkEmail({
        companyId: bookingData.companyData.id,
        companyName: bookingData.companyData.name,
        toEmail: bookingData.customer.email,
        toName: bookingData.customer.name,
        serviceName: bookingData.selectedService?.name,
        employeeName: employeeDisplayName,
        dateTime: bookingData.selectedDateTime ?? bookingData.dateTime,
        shareUrl: shareableLink,
      });
      const until = result?.cooldownUntil ?? Date.now() + EMAIL_COOLDOWN_MS;
      setEmailCooldownUntil(until);
      showToast('Email sent to customer', 'success');
    } catch (e) {
      console.error('Failed to send email:', e);
      const msg = e?.message || 'Failed to send email';
      showToast(typeof msg === 'string' ? msg : 'Failed to send email', 'error');
      const until = e?.details?.cooldownUntil ?? (e?.code === 'functions/resource-exhausted' ? Date.now() + EMAIL_COOLDOWN_MS : null);
      if (until) setEmailCooldownUntil(until);
    } finally {
      setSendingEmail(false);
    }
  };

  // Cooldown timer tick and label
  useEffect(() => {
    if (emailCooldownUntil == null) return;
    const tick = () => {
      const now = Date.now();
      if (now >= emailCooldownUntil) {
        setEmailCooldownUntil(null);
        setEmailCooldownLabel('');
        return;
      }
      setEmailCooldownLabel(formatCooldown(emailCooldownUntil - now));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [emailCooldownUntil]);

  // Fallback: fetch staff photo from Firestore when not in bookingData (e.g. older bookings)
  const staffPhotoFromData =
    bookingData?.selectedEmployee?.actualEmployee?.photoURL ||
    bookingData?.selectedEmployee?.photoURL ||
    null;
  const employeeId =
    bookingData?.employeeId ||
    bookingData?.selectedEmployee?.employeeId ||
    bookingData?.selectedEmployee?.id;
  const companyId = bookingData?.companyData?.id;

  useEffect(() => {
    if (staffPhotoFromData || !employeeId || employeeId === 'any' || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const employee = await getEmployee({ companyId, id: employeeId });
        if (!cancelled && employee?.photoURL) {
          setFetchedStaffPhotoURL(employee.photoURL);
        }
      } catch (err) {
        if (!cancelled) console.warn('Failed to fetch staff photo for success page:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, employeeId, staffPhotoFromData]);

  useEffect(() => {
    if (!employeeId || employeeId === 'any' || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const employee = await getEmployee({ companyId, id: employeeId });
        if (cancelled) return;
        const ratings = employee?.ratings;
        const averageRating = employee?.averageRating != null
          ? Number(employee.averageRating)
          : Array.isArray(ratings) && ratings.length > 0
            ? ratings.reduce((acc, r) => acc + (r.rating || 0), 0) / ratings.length
            : null;
        const totalRatings = employee?.totalRatings != null
          ? Number(employee.totalRatings)
          : Array.isArray(ratings) ? ratings.length : 0;
        if (averageRating != null && totalRatings > 0) {
          setStaffRating({ averageRating, totalRatings });
        } else {
          setStaffRating({ averageRating: 0, totalRatings: 0 });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to fetch staff ratings:', err);
          setStaffRating({ averageRating: 0, totalRatings: 0 });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, employeeId]);

  if (!bookingData) {
    return null;
  }

  const formattedDate = format(bookingData.selectedDateTime, 'EEEE, MMMM d, yyyy');
  const formattedTime = format(bookingData.selectedDateTime, 'h:mm a');
  const service = bookingData.selectedService || {};
  const employeeDisplayName =
    bookingData.selectedEmployee?.actualEmployeeName ||
    bookingData.employeeName ||
    bookingData.selectedEmployee?.name ||
    'Staff';
  const staffPhotoURL = staffPhotoFromData || fetchedStaffPhotoURL;
  const staffInitial = (employeeDisplayName || 'S').trim().charAt(0).toUpperCase();
  const serviceLine = `${service.name} with ${employeeDisplayName}`;
  const selectedAddons = service.selectedAddons || [];
  const selectedPackage = service.selectedPackage || null;
  const basePrice = selectedPackage ? (selectedPackage.price ?? 0) : (service.price ?? 0);
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
  const totalPrice = Number(basePrice) + addonsTotal;

  const totalDuration = (() => {
    if (selectedPackage?.duration != null) {
      const pkgDuration = parseInt(selectedPackage.duration) || 0;
      const pkgBuffer = parseInt(selectedPackage.bufferTime) || 0;
      const addonsMins = (selectedAddons || []).reduce(
        (sum, a) => sum + (parseInt(a.duration) || 0) + (parseInt(a.bufferTime) || 0),
        0
      );
      return pkgDuration + pkgBuffer + addonsMins;
    }
    const svcDuration = parseInt(service.duration) || 60;
    const svcBuffer = parseInt(service.bufferTime) || 0;
    const addonsMins = (selectedAddons || []).reduce(
      (sum, a) => sum + (parseInt(a.duration) || 0) + (parseInt(a.bufferTime) || 0),
      0
    );
    return svcDuration + svcBuffer + addonsMins;
  })();

  return (
    <div
      className="manual-success-root"
      data-theme={mode === 'dark' ? 'dark' : 'light'}
      style={themeVars}
    >
      <div className="manual-success-container">
        <header className="manual-success-header">
          <h1 className="manual-success-title">Booking Confirmed</h1>
          <p className="manual-success-subtitle">
            The appointment has been successfully scheduled
          </p>
        </header>

        <article className="manual-success-card">
          {/* Customer Information */}
          <section className="manual-success-block manual-success-block-muted">
            <h2 className="manual-success-overline">Customer Information</h2>
            <div className="manual-success-grid manual-success-grid-3">
              <div className="manual-success-field">
                <p className="manual-success-field-label">Name</p>
                <p className="manual-success-field-value">{bookingData.customer.name}</p>
              </div>
              <div className="manual-success-field">
                <p className="manual-success-field-label">Email</p>
                <p className="manual-success-field-value">{bookingData.customer.email}</p>
              </div>
              <div className="manual-success-field">
                <p className="manual-success-field-label">Phone</p>
                <p className="manual-success-field-value">{formatPhoneNumber(bookingData.customer.phone)}</p>
              </div>
            </div>
          </section>

          {/* Service & Staff (two columns) */}
          <div className="manual-success-two-col">
            <section className="manual-success-block manual-success-block-service">
              <h2 className="manual-success-overline">Service</h2>
              <p className="manual-success-service-name">{service.name}</p>
              <div className="manual-success-meta-row">
                <span className="manual-success-meta-label">Duration</span>
                <span className="manual-success-meta-value">{totalDuration} min</span>
              </div>
              <div className="manual-success-meta-row">
                <span className="manual-success-meta-label">Base Price</span>
                <span className="manual-success-meta-value manual-success-price">${Number(basePrice).toFixed(2)}</span>
              </div>
              {selectedAddons.length > 0 && (
                <div className="manual-success-meta-row manual-success-addons-inline">
                  <span className="manual-success-meta-label">Add-ons</span>
                  <div className="manual-success-meta-value">
                    {selectedAddons.map((addon, idx) => (
                      <span key={addon.id || idx}>
                        {addon.name} <span className="manual-success-price">(+${Number(addon.price || 0).toFixed(2)})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedPackage && (
                <div className="manual-success-meta-row">
                  <span className="manual-success-meta-label">Package</span>
                  <span className="manual-success-meta-value">{selectedPackage.name}</span>
                </div>
              )}
              {totalPrice > 0 && (
                <div className="manual-success-total-row">
                  <span className="manual-success-meta-label">Total Price</span>
                  <span className="manual-success-total-value">${totalPrice.toFixed(2)}</span>
                </div>
              )}
            </section>
            <section className="manual-success-block manual-success-block-staff">
              <h2 className="manual-success-overline">Staff Member</h2>
              <div className="manual-success-staff-row">
                <div className="manual-success-staff-avatar">
                  {staffPhotoURL ? (
                    <img src={staffPhotoURL} alt={employeeDisplayName} />
                  ) : (
                    <span className="manual-success-staff-initial">{staffInitial}</span>
                  )}
                </div>
                <p className="manual-success-staff-name">{employeeDisplayName}</p>
                <div className="manual-success-staff-rating">
                  <span className="manual-success-staff-stars" aria-label={staffRating ? `${staffRating.averageRating.toFixed(1)} out of 5 stars` : '0 out of 5 stars'}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span key={i} className={staffRating && i <= Math.round(staffRating.averageRating) ? 'filled' : ''}>★</span>
                    ))}
                  </span>
                  <span className="manual-success-staff-rating-text">
                    {staffRating && staffRating.totalRatings > 0
                      ? `${staffRating.averageRating.toFixed(1)} · ${staffRating.totalRatings} ${staffRating.totalRatings === 1 ? 'review' : 'reviews'}`
                      : '0/5 stars · No ratings yet'}
                  </span>
                </div>
              </div>
            </section>
          </div>

          {/* Date & Time */}
          <section className="manual-success-block manual-success-block-muted">
            <h2 className="manual-success-overline">Date & Time</h2>
            <div className="manual-success-grid manual-success-grid-2">
              <div className="manual-success-field">
                <p className="manual-success-field-label">Date</p>
                <p className="manual-success-field-value">{formattedDate}</p>
              </div>
              <div className="manual-success-field">
                <p className="manual-success-field-label">Time</p>
                <p className="manual-success-field-value">{formattedTime}</p>
              </div>
            </div>
          </section>
        </article>

        {shareableLink ? (
          <article className="manual-success-card manual-success-share-card">
            <h2 className="manual-success-share-title">Share Appointment Details</h2>
            <p className="manual-success-share-desc">
              Share this link with your customer to view their appointment details:
            </p>
            <div className="manual-success-share-block">
              <span className="manual-success-share-url" title={shareableLink}>
                {shareableLink}
              </span>
            </div>
            <div className="manual-success-share-buttons">
              <div className="manual-success-email-btn-wrap">
                <button
                  type="button"
                  className="manual-success-btn-secondary"
                  onClick={handleEmailLink}
                  disabled={sendingEmail || !!emailCooldownLabel}
                >
                  <EmailIcon />
                  {sendingEmail ? 'Sending…' : 'Email to Customer'}
                </button>
                {emailCooldownLabel && (
                  <span className="manual-success-email-cooldown" aria-live="polite">
                    Try again in {emailCooldownLabel}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="manual-success-btn-success"
                onClick={handleCopyLink}
              >
                <CopyIcon />
                Copy Link
              </button>
            </div>
          </article>
        ) : (
          <article className="manual-success-card manual-success-share-card">
            <h2 className="manual-success-share-title">Share Appointment Details</h2>
            <p className="manual-success-share-desc">
              Set up your website name in Client Experience settings to share a booking link with customers.
            </p>
          </article>
        )}

        <div className="manual-success-cta-wrap">
          <button
            type="button"
            className="manual-success-btn-primary"
            onClick={() => navigate('/app/overview')}
          >
            <DashboardIcon />
            Go to Overview
          </button>
        </div>
      </div>

    </div>
  );
};

export default ManualBookingSuccessPage;
